import { Box, IconButton as MuiIconButton, Typography } from '@mui/material';
import { VIEW_DRAFT, VIEW_ARTICLE } from 'constants/routes';
import { Link } from 'react-router-dom';
import ThumbUpFillIcon from 'remixicon-react/ThumbUpFillIcon';
import ThumbUpLineIcon from 'remixicon-react/ThumbUpLineIcon';
import { styled } from '@mui/material/styles';
import useUserData from 'hooks/useUserData';
import { ArticleType } from '../hooks/useArticlesQuery';
import { ArticleVariantType } from './ArticleBox';
import { useMemo } from 'react';
import Chat3FillIcon from 'remixicon-react/Chat3FillIcon';

type ArticleInteractionActionsProps = {
  article: ArticleType;
  isDetailedView?: boolean;
  isDraftView?: boolean;
  isRelatedPostView?: boolean;
  scrollInfoKey?: string;
  searchKeyword?: string;
  variant: ArticleVariantType;
  likeHandler: (article?: ArticleType) => void;
  viewInteractionsHandler: () => void;
};

function ArticleInteractionActions({
  article,
  isDetailedView,
  isDraftView,
  isRelatedPostView,
  scrollInfoKey,
  searchKeyword,
  variant,
  likeHandler,
  viewInteractionsHandler,
}: ArticleInteractionActionsProps) {
  const { isNovaAdmin, user } = useUserData();
  const canViewInteractions = useMemo(
    () => isNovaAdmin || user?.username === article.createdBy,
    [isNovaAdmin, user?.username, article.createdBy],
  );

  return (
    <Box display="flex" alignItems="center" mr={variant === 'playlist' ? 0 : -1.25}>
      {variant === 'playlist' && article.totalComments > 0 && (
        <Box
          {...(!isDetailedView && {
            component: Link,
            to: `${(isDraftView ? VIEW_DRAFT : VIEW_ARTICLE).replace(':articleId', article.id)}${
              searchKeyword ? `?q=${encodeURIComponent(searchKeyword)}` : ''
            }#comments`,
            state: {
              isPlayListFeature: article.originIndicator === 'PLAYLIST',
              scrollInfoKey,
              isDraft: article.isDraft,
            },
          })}
        >
          <IconButton
            size="small"
            className={`${isDetailedView ? 'no-hover cursor-default' : ''} ${variant}`}
            disableRipple={isDetailedView}
            sx={(theme) => ({ color: theme.palette.custom.paleSky })}
          >
            <Chat3FillIcon size={18} />
            <$Count className={variant}>{article.totalComments}</$Count>
          </IconButton>
        </Box>
      )}

      <IconButton
        onClick={() => {
          if (article.createdBy === user?.username || isNovaAdmin) {
            viewInteractionsHandler();
          } else {
            likeHandler(article);
          }
        }}
        size="small"
        sx={(theme) => ({
          mr: isRelatedPostView ? 0 : 0.5,
          color: theme.palette.custom.paleSky,
        })}
      >
        {article.userLiked ? <ThumbUpFillIcon size={18} /> : <ThumbUpLineIcon size={18} />}
      </IconButton>
    </Box>
  );
}

const IconButton = styled(MuiIconButton, { label: 'IconButton' })(({ theme }) => ({
  color: theme.palette.custom.mediumBlack,
  borderRadius: 20,
  padding: '2px 12px',
}));

const $Count = styled(Typography, { label: 'Count' })({
  marginLeft: 2,
  '&.related-post': {
    fontSize: 14,
  },
});

export default ArticleInteractionActions;
